import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Globe, Palette, CurrencyDollar, WhatsappLogo, Clock, Headset, FileText, CreditCard } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  active: { label: 'Ativo', color: 'bg-green-100 text-green-700' },
  trial: { label: 'Teste Grátis', color: 'bg-blue-100 text-blue-700' },
  lifetime: { label: 'Vitalício', color: 'bg-purple-100 text-purple-700' },
  overdue: { label: 'Inadimplente', color: 'bg-yellow-100 text-yellow-700' },
  cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-700' },
};

const Settings = () => {
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(false);
  const [language, setLanguage] = useState('pt-BR');
  const [currency, setCurrency] = useState('BRL');
  const [morningSummaryEnabled, setMorningSummaryEnabled] = useState(false);
  const [morningSummaryTime, setMorningSummaryTime] = useState('08:00');
  const [savingSummary, setSavingSummary] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    const isDark = document.documentElement.classList.contains('dark');
    setDarkMode(isDark);
    
    // Fetch user's morning summary settings
    const fetchSettings = async () => {
      try {
        const [summaryRes, subRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/tasks/today-summary`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/subscription/status`, { withCredentials: true })
        ]);
        setMorningSummaryEnabled(summaryRes.data.morning_summary_enabled || false);
        setMorningSummaryTime(summaryRes.data.morning_summary_time || '08:00');
        setSubscription(subRes.data);
      } catch (error) {
        // silently fail
      }
    };
    fetchSettings();
  }, []);

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/subscription/cancel`, {}, { withCredentials: true });
      toast.success(res.data.message);
      setCancelDialogOpen(false);
      setSubscription(prev => ({ ...prev, subscription_status: 'cancelled' }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cancelar assinatura');
    } finally {
      setCancelling(false);
    }
  };

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    if (newMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', newMode.toString());
    toast.success(`Modo ${newMode ? 'escuro' : 'claro'} ativado`);
  };

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
    toast.success('Idioma atualizado');
  };

  const handleCurrencyChange = (curr) => {
    setCurrency(curr);
    localStorage.setItem('currency', curr);
    toast.success('Moeda atualizada');
  };

  const handleSaveMorningSummary = async () => {
    setSavingSummary(true);
    try {
      await axios.patch(`${BACKEND_URL}/api/profile`, {
        morning_summary_enabled: morningSummaryEnabled,
        morning_summary_time: morningSummaryTime
      }, { withCredentials: true });
      toast.success('Configuração do resumo salva!');
    } catch (error) {
      toast.error('Erro ao salvar configuração');
    } finally {
      setSavingSummary(false);
    }
  };

  return (
    <div className="p-3 sm:p-6 md:p-8 space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-1 sm:mb-2">Configurações</h1>
        <p className="text-sm sm:text-base text-muted-foreground">Personalize sua experiência</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Subscription Plan */}
        <Card className="border-2 border-primary/20 md:col-span-2">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <CreditCard size={24} className="text-primary" />
              <CardTitle>Meu Plano</CardTitle>
            </div>
            <CardDescription>Gerencie sua assinatura</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <span className="text-lg font-semibold">Plano Aretê</span>
                  {subscription && (
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_LABELS[subscription.subscription_status]?.color || 'bg-gray-100 text-gray-600'}`}>
                      {STATUS_LABELS[subscription.subscription_status]?.label || subscription.subscription_status}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">R$ 29,99/mês</p>
                {subscription?.expiration_date && (
                  <p className="text-xs text-muted-foreground">
                    {subscription.subscription_status === 'cancelled' ? 'Acesso até: ' : 'Próxima cobrança: '}
                    {new Date(subscription.expiration_date).toLocaleDateString('pt-BR')}
                  </p>
                )}
              </div>
              {subscription?.mp_preapproval_id && subscription?.subscription_status !== 'cancelled' && (
                <button
                  onClick={() => setCancelDialogOpen(true)}
                  className="text-xs text-gray-400 hover:text-gray-500 underline self-end sm:self-center"
                  data-testid="cancel-subscription-btn"
                >
                  Cancelar assinatura
                </button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Cancel Subscription Dialog */}
        <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cancelar Assinatura</DialogTitle>
              <DialogDescription>
                Tem certeza que deseja cancelar sua assinatura? Você ainda terá acesso até o final do período já pago.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <p className="text-sm text-muted-foreground">
                Ao cancelar, você perderá acesso a todas as funcionalidades ao final do ciclo atual, incluindo o bot WhatsApp.
              </p>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
                  Manter Plano
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleCancelSubscription}
                  disabled={cancelling}
                  data-testid="confirm-cancel-subscription-btn"
                >
                  {cancelling ? 'Cancelando...' : 'Confirmar Cancelamento'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        {/* Appearance */}
        <Card className="opacity-60">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <Palette size={24} className="text-primary" />
              <CardTitle>Aparência</CardTitle>
              <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-medium">Em desenvolvimento</span>
            </div>
            <CardDescription>Personalize o tema do aplicativo</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="dark-mode">Modo Escuro</Label>
                <p className="text-sm text-muted-foreground">Em breve</p>
              </div>
              <Switch
                id="dark-mode"
                checked={false}
                disabled
                data-testid="dark-mode-toggle"
              />
            </div>
          </CardContent>
        </Card>

        {/* Language */}
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-2">
              <Globe size={24} className="text-primary" />
              <CardTitle>Idioma</CardTitle>
            </div>
            <CardDescription>Selecione seu idioma preferido</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="language">Idioma do Sistema</Label>
              <Select value={language} onValueChange={handleLanguageChange}>
                <SelectTrigger id="language" data-testid="language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pt-BR">Português (Brasil)</SelectItem>
                  <SelectItem value="en-US">English (US)</SelectItem>
                  <SelectItem value="es-ES">Español</SelectItem>
                  <SelectItem value="fr-FR">Français</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-2">
                * Alguns recursos podem não estar disponíveis em todos os idiomas
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Currency */}
        <Card className="opacity-60">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <CurrencyDollar size={24} className="text-primary" />
              <CardTitle>Moeda Principal</CardTitle>
              <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-medium">Em desenvolvimento</span>
            </div>
            <CardDescription>Defina a moeda padrão para transações</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="currency">Moeda</Label>
              <Select value="BRL" disabled>
                <SelectTrigger id="currency" data-testid="currency-select" disabled>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="BRL">Real Brasileiro (BRL)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Mais moedas em breve</p>
            </div>
          </CardContent>
        </Card>

        {/* WhatsApp Morning Summary */}
        <Card className="border-2 border-green-100">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <WhatsappLogo size={24} className="text-green-500" />
              <CardTitle>Resumo Matinal</CardTitle>
            </div>
            <CardDescription>Receba um resumo das tarefas do dia via WhatsApp</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="morning-summary">Ativar Resumo</Label>
                <p className="text-xs text-muted-foreground">Envio automático pelo WhatsApp</p>
              </div>
              <Switch
                id="morning-summary"
                checked={morningSummaryEnabled}
                onCheckedChange={(v) => setMorningSummaryEnabled(v)}
                data-testid="morning-summary-toggle"
              />
            </div>
            {morningSummaryEnabled && (
              <div className="space-y-2">
                <Label htmlFor="summary-time" className="flex items-center gap-1.5">
                  <Clock size={14} className="text-muted-foreground" />
                  Horário do Envio
                </Label>
                <Input
                  id="summary-time"
                  type="time"
                  value={morningSummaryTime}
                  onChange={e => setMorningSummaryTime(e.target.value)}
                  data-testid="morning-summary-time"
                />
                <p className="text-[10px] text-muted-foreground">
                  O resumo será enviado diariamente neste horário para o WhatsApp vinculado à sua conta
                </p>
              </div>
            )}
            <Button
              onClick={handleSaveMorningSummary}
              disabled={savingSummary}
              className="w-full"
              data-testid="save-morning-summary-btn"
            >
              {savingSummary ? 'Salvando...' : 'Salvar Configuração'}
            </Button>
          </CardContent>
        </Card>

        {/* Support & Contact */}
        <Card className="border-2 border-orange-100">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <Headset size={24} className="text-orange-500" />
              <CardTitle>Suporte e Contato</CardTitle>
            </div>
            <CardDescription>Entre em contato para dúvidas, problemas ou sugestões</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <a 
                href="mailto:suporte@aretesystem.com.br" 
                className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                data-testid="settings-support-email"
              >
                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                </div>
                <div>
                  <p className="text-sm font-medium">Suporte</p>
                  <p className="text-xs text-muted-foreground">suporte@aretesystem.com.br</p>
                </div>
              </a>
            </div>
            <p className="text-xs text-muted-foreground">
              Responderemos em até 24 horas úteis. Para erros, inclua capturas de tela.
            </p>
          </CardContent>
        </Card>

        {/* Legal / About */}
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-2">
              <FileText size={24} className="text-primary" />
              <CardTitle>Legal e Sobre</CardTitle>
            </div>
            <CardDescription>Informações legais e do aplicativo</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Versão</span>
                <span className="font-medium">1.0.0</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Site</span>
                <a href="https://aretesystem.com.br" target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">aretesystem.com.br</a>
              </div>
            </div>
            <div className="pt-3 border-t space-y-2">
              <Button variant="outline" className="w-full justify-start gap-2" onClick={() => navigate('/termos')} data-testid="settings-terms-link">
                <FileText size={16} />
                Termos de Uso
              </Button>
              <Button variant="outline" className="w-full justify-start gap-2" onClick={() => navigate('/privacidade')} data-testid="settings-privacy-link">
                <FileText size={16} />
                Política de Privacidade
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Settings;
